import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  PieChart,
  Target,
  Clock,
  Users,
  MapPin,
  Calendar,
  DollarSign,
  Activity,
  RefreshCw,
  Download,
  Filter,
  ArrowUp,
  ArrowDown,
  Minus,
  Building,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

// Import types from your existing types file
import {
  PerformanceAnalytics,
  ProgressTracking,
  DistrictSummary,
  StageSummary,
  WorkStage,
  GrantLevel
} from '../../types/work.types';

// Import API service
import { worksApiService } from '../../services/workApi.service';

// ==========================================
// CHART COMPONENTS
// ==========================================

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  loading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  trend, 
  icon: Icon, 
  color,
  loading = false 
}) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600'
  };

  const bgClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200'
  };

  if (loading) {
    return (
      <div className={`relative overflow-hidden rounded-xl border-2 ${bgClasses[color]} p-6 animate-pulse`}>
        <div className="h-8 bg-gray-300 rounded mb-2"></div>
        <div className="h-6 bg-gray-300 rounded w-3/4"></div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-xl border-2 ${bgClasses[color]} p-6 shadow-sm hover:shadow-md transition-all duration-300`}>
      <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${colorClasses[color]} opacity-10 rounded-bl-full`}></div>
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <Icon className="w-8 h-8 text-gray-700" />
          {trend !== undefined && (
            <div className={`flex items-center text-sm font-medium ${
              trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {trend > 0 ? <ArrowUp className="w-4 h-4 mr-1" /> : 
               trend < 0 ? <ArrowDown className="w-4 h-4 mr-1" /> : 
               <Minus className="w-4 h-4 mr-1" />}
              {Math.abs(trend).toFixed(1)}%
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          <p className="text-2xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
          </p>
          <p className="text-sm font-medium text-gray-700">{title}</p>
          {subtitle && <p className="text-xs text-gray-600">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const ContractorPerformanceChart: React.FC<{ 
  data: PerformanceAnalytics['contractorPerformance'];
  loading?: boolean;
}> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="p-4 bg-gray-200 rounded-lg h-24"></div>
        ))}
      </div>
    );
  }

  const top10Contractors = data.slice(0, 10);
  const maxValue = Math.max(...top10Contractors.map(c => c.total_contract_value));

  return (
    <div className="space-y-4">
      {top10Contractors.map((contractor, index) => {
        const completionRate = contractor.total_works > 0 ? (contractor.completed_works / contractor.total_works) * 100 : 0;
        const delayRate = contractor.total_works > 0 ? (contractor.delayed_works / contractor.total_works) * 100 : 0;
        
        return (
          <div key={contractor.contractor_id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                  index < 3 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                  index < 6 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                  'bg-gradient-to-r from-gray-400 to-gray-600'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{contractor.contractor_name}</h4>
                  <p className="text-sm text-gray-600">{contractor.total_works} works assigned</p>
                </div>
              </div>
              
              <div className="text-right">
                <p className="font-semibold text-gray-900">
                  {worksApiService.formatCurrency(contractor.total_contract_value)}
                </p>
                <div className="flex items-center space-x-2 text-xs">
                  <span className={`px-2 py-1 rounded-full ${
                    completionRate >= 80 ? 'bg-green-100 text-green-800' :
                    completionRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {completionRate.toFixed(0)}% complete
                  </span>
                  {delayRate > 0 && (
                    <span className="px-2 py-1 rounded-full bg-red-100 text-red-800">
                      {delayRate.toFixed(0)}% delayed
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Contract Value</span>
                <span>{((contractor.total_contract_value / maxValue) * 100).toFixed(0)}% of top performer</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(contractor.total_contract_value / maxValue) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900">{contractor.completed_works}</p>
                <p className="text-xs text-gray-600">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900">
                  {worksApiService.formatCurrency(contractor.total_expenditure)}
                </p>
                <p className="text-xs text-gray-600">Spent</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900">
                  {contractor.avg_project_duration ? Math.round(contractor.avg_project_duration) : 0} days
                </p>
                <p className="text-xs text-gray-600">Avg Duration</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const WorkTypeAnalysisChart: React.FC<{ 
  data: PerformanceAnalytics['workTypeAnalysis'];
  loading?: boolean;
}> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-4 bg-gray-200 rounded-lg h-32"></div>
        ))}
      </div>
    );
  }

  const totalWorks = data.reduce((sum, item) => sum + item.work_count, 0);
  const colors = [
    'from-blue-400 to-blue-600', 
    'from-green-400 to-green-600', 
    'from-purple-400 to-purple-600', 
    'from-orange-400 to-orange-600'
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {data.map((workType, index) => {
        const percentage = totalWorks > 0 ? (workType.work_count / totalWorks) * 100 : 0;
        const completionRate = workType.work_count > 0 ? (workType.completed_count / workType.work_count) * 100 : 0;
        
        return (
          <div key={`${workType.work_type}-${workType.work_sub_type}`} className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900">{workType.work_type}</h4>
                {workType.work_sub_type && (
                  <p className="text-sm text-gray-600">{workType.work_sub_type}</p>
                )}
              </div>
              <span className="text-lg font-bold text-gray-900">{workType.work_count}</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Share of Total</span>
                <span className="font-medium">{percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`bg-gradient-to-r ${colors[index % colors.length]} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Total Cost</p>
                  <p className="font-semibold">{worksApiService.formatCurrency(workType.total_cost)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Avg Cost</p>
                  <p className="font-semibold">{worksApiService.formatCurrency(workType.avg_cost)}</p>
                </div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Completion Rate</span>
                <span className={`font-medium ${
                  completionRate >= 80 ? 'text-green-600' :
                  completionRate >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {completionRate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TimeAnalysisChart: React.FC<{ 
  data: PerformanceAnalytics['timeAnalysis'];
  loading?: boolean;
}> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
          ))}
        </div>
        <div className="bg-gray-200 h-64 rounded-lg"></div>
      </div>
    );
  }

  const last12Months = data.slice(0, 12);
  const maxAmount = Math.max(...last12Months.map(m => m.total_sanctioned_amount));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-900">
            {last12Months.reduce((sum, m) => sum + m.works_sanctioned, 0)}
          </p>
          <p className="text-sm text-blue-700">Total Works (12M)</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-lg font-bold text-green-900">
            {worksApiService.formatCurrency(last12Months.reduce((sum, m) => sum + m.total_sanctioned_amount, 0))}
          </p>
          <p className="text-sm text-green-700">Total Sanctioned</p>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <p className="text-2xl font-bold text-purple-900">
            {last12Months.reduce((sum, m) => sum + m.completed_works, 0)}
          </p>
          <p className="text-sm text-purple-700">Completed Works</p>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <p className="text-2xl font-bold text-orange-900">
            {Math.round(last12Months.reduce((sum, m) => sum + (m.avg_completion_time_days || 0), 0) / last12Months.length)}
          </p>
          <p className="text-sm text-orange-700">Avg Days to Complete</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="flex space-x-4 pb-4" style={{ minWidth: '1000px' }}>
          {last12Months.map((month, index) => (
            <div key={`${month.year}-${month.month}`} className="flex-shrink-0 bg-white border border-gray-200 rounded-lg p-4 min-w-[200px]">
              <div className="text-center mb-4">
                <h4 className="font-semibold text-gray-900">
                  {new Date(month.year, month.month - 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                </h4>
              </div>
              
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{month.works_sanctioned}</div>
                  <div className="text-xs text-gray-600">works sanctioned</div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm font-semibold text-green-600">
                    {worksApiService.formatCurrency(month.total_sanctioned_amount)}
                  </div>
                  <div className="text-xs text-gray-600">amount sanctioned</div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm font-semibold text-purple-600">{month.completed_works}</div>
                  <div className="text-xs text-gray-600">completed</div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm font-semibold text-orange-600">
                    {worksApiService.formatCurrency(month.avg_work_cost)}
                  </div>
                  <div className="text-xs text-gray-600">avg cost</div>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-400 to-green-400 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(month.total_sanctioned_amount / maxAmount) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CostVarianceAnalysis: React.FC<{ 
  data: PerformanceAnalytics['costVarianceAnalysis'];
  loading?: boolean;
}> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="bg-gray-200 h-8 rounded mb-4"></div>
        {[...Array(10)].map((_, i) => (
          <div key={i} className="bg-gray-200 h-12 rounded mb-2"></div>
        ))}
      </div>
    );
  }

  const top20 = data.slice(0, 20);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Original Cost</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">RAS Amount</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Spent</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Escalation</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {top20.map((item) => (
            <tr key={item.work_id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900">SNO: {item.sno}</p>
                  <p className="text-sm text-gray-600 truncate max-w-xs" title={item.work_name}>
                    {item.work_name}
                  </p>
                  <p className="text-xs text-gray-500">{item.district_name}</p>
                </div>
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {worksApiService.formatCurrency(item.estimate_cost)}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {item.ras_amount ? worksApiService.formatCurrency(item.ras_amount) : '-'}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {worksApiService.formatCurrency(item.cum_exp_on_work)}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                <div className={`${
                  item.escalation_percentage > 20 ? 'text-red-600' :
                  item.escalation_percentage > 10 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {item.escalation_percentage > 0 ? '+' : ''}{item.escalation_percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500">
                  {worksApiService.formatCurrency(item.cost_escalation)}
                </div>
              </td>
              <td className="px-4 py-3 text-right text-sm">
                <div className={`${
                  item.variance_percentage > 10 ? 'text-red-600' :
                  item.variance_percentage > 5 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {item.variance_percentage > 0 ? '+' : ''}{item.variance_percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500">
                  {worksApiService.formatCurrency(item.expenditure_variance)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==========================================
// MAIN ANALYTICS COMPONENT
// ==========================================

const Analytics: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceAnalytics | null>(null);
  const [progressData, setProgressData] = useState<ProgressTracking | null>(null);
  const [districtData, setDistrictData] = useState<DistrictSummary[]>([]);
  const [stageData, setStageData] = useState<StageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'performance' | 'progress' | 'comparison' | 'variance'>('performance');

  // ==========================================
  // DATA FETCHING
  // ==========================================

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [performance, progress, districts, stages] = await Promise.all([
        worksApiService.getPerformanceAnalytics(),
        worksApiService.getProgressTracking(),
        worksApiService.getDistrictWiseSummary(),
        worksApiService.getStageWiseSummary()
      ]);

      setPerformanceData(performance);
      setProgressData(progress);
      setDistrictData(districts);
      setStageData(stages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  const handleRefresh = useCallback(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleExport = useCallback(async () => {
    try {
      const exportData = {
        performance: performanceData,
        progress: progressData,
        districts: districtData,
        stages: stageData,
        exportedAt: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export analytics');
    }
  }, [performanceData, progressData, districtData, stageData]);

  // ==========================================
  // CALCULATE OVERVIEW METRICS
  // ==========================================

  const overviewMetrics = useMemo(() => {
    const totalWorks = stageData.reduce((sum, stage) => sum + stage.work_count, 0);
    const totalValue = stageData.reduce((sum, stage) => sum + stage.total_estimate_cost, 0);
    const totalSpent = stageData.reduce((sum, stage) => sum + stage.total_expenditure, 0);
    const completedWorks = stageData.find(s => s.abstract_stage_of_work === WorkStage.COMPLETED)?.work_count || 0;
    const inProgressWorks = stageData.find(s => s.abstract_stage_of_work === WorkStage.IN_PROGRESS)?.work_count || 0;
    const delayedWorks = progressData?.delayedWorks?.length || 0;
    const utilizationRate = totalValue > 0 ? (totalSpent / totalValue) * 100 : 0;

    return {
      totalWorks,
      totalValue,
      totalSpent,
      completedWorks,
      inProgressWorks,
      delayedWorks,
      utilizationRate
    };
  }, [stageData, progressData]);

  // ==========================================
  // RENDER ERROR STATE
  // ==========================================

  if (error && !performanceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <BarChart3 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER MAIN COMPONENT
  // ==========================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics & Performance</h1>
              <p className="text-gray-600 mt-1">
                Comprehensive insights into works performance and trends
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleExport}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={Building}
            title="Total Works"
            value={overviewMetrics.totalWorks}
            subtitle="All active projects"
            color="blue"
            loading={loading}
          />
          <MetricCard
            icon={DollarSign}
            title="Total Value"
            value={worksApiService.formatCurrency(overviewMetrics.totalValue)}
            subtitle={`${overviewMetrics.utilizationRate.toFixed(1)}% utilized`}
            trend={overviewMetrics.utilizationRate - 75}
            color="green"
            loading={loading}
          />
          <MetricCard
            icon={Target}
            title="Completion Rate"
            value={`${overviewMetrics.totalWorks > 0 ? ((overviewMetrics.completedWorks / overviewMetrics.totalWorks) * 100).toFixed(1) : 0}%`}
            subtitle={`${overviewMetrics.completedWorks} of ${overviewMetrics.totalWorks} completed`}
            color="purple"
            loading={loading}
          />
          <MetricCard
            icon={Clock}
            title="Works at Risk"
            value={overviewMetrics.delayedWorks}
            subtitle="Past deadline"
            trend={overviewMetrics.delayedWorks > 0 ? -(overviewMetrics.delayedWorks / overviewMetrics.totalWorks) * 100 : 0}
            color="red"
            loading={loading}
          />
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-800">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'performance', label: 'Contractor Performance', icon: Users },
                { id: 'progress', label: 'Progress Analysis', icon: TrendingUp },
                { id: 'comparison', label: 'District Comparison', icon: MapPin },
                { id: 'variance', label: 'Cost Variance', icon: DollarSign }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'performance' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Top Performing Contractors
                  </h3>
                  <ContractorPerformanceChart 
                    data={performanceData?.contractorPerformance || []} 
                    loading={loading}
                  />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Work Type Distribution & Performance
                  </h3>
                  <WorkTypeAnalysisChart 
                    data={performanceData?.workTypeAnalysis || []} 
                    loading={loading}
                  />
                </div>
              </div>
            )}

            {activeTab === 'progress' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Monthly Performance Trends
                  </h3>
                  <TimeAnalysisChart 
                    data={performanceData?.timeAnalysis || []} 
                    loading={loading}
                  />
                </div>
                
                {progressData?.delayedWorks && progressData.delayedWorks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                      Works Requiring Attention ({progressData.delayedWorks.length})
                    </h3>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="grid gap-3">
                        {progressData.delayedWorks.slice(0, 10).map((work) => (
                          <div key={work.work_id} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                            <div>
                              <p className="font-medium text-gray-900">SNO: {work.sno}</p>
                              <p className="text-sm text-gray-600 truncate max-w-md">{work.work_name}</p>
                              <p className="text-xs text-gray-500">{work.district_name} • {work.agency_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-red-600">
                                Due: {worksApiService.formatDate(work.completion_date_set)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {Math.floor((new Date().getTime() - new Date(work.completion_date_set).getTime()) / (1000 * 60 * 60 * 24))} days overdue
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {progressData.delayedWorks.length > 10 && (
                        <p className="text-center text-sm text-gray-600 mt-4">
                          and {progressData.delayedWorks.length - 10} more delayed works...
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'comparison' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  District-wise Performance Comparison
                </h3>
                
                {loading ? (
                  <div className="grid gap-4 animate-pulse">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="bg-gray-200 rounded-lg h-32"></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {districtData.map((district, index) => (
                      <div key={district.district_id} className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              index < 3 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                              index < 6 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                              'bg-gradient-to-r from-gray-500 to-gray-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">{district.district_name}</h4>
                              <p className="text-sm text-gray-600">{district.total_works} works</p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              {worksApiService.formatCurrency(district.total_expenditure)}
                            </p>
                            <p className="text-sm text-gray-600">total expenditure</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="text-center">
                            <p className="text-xl font-bold text-blue-600">
                              {worksApiService.formatCurrency(district.total_estimate_cost)}
                            </p>
                            <p className="text-xs text-gray-600">Sanctioned</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-green-600">
                              {district.financial_progress_percentage.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-600">Financial Progress</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-purple-600">
                              {district.physical_progress_percentage.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-600">Physical Progress</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-orange-600">
                              {worksApiService.formatCurrency(district.avg_estimate_cost)}
                            </p>
                            <p className="text-xs text-gray-600">Avg Cost</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Financial Progress</span>
                            <span className="font-medium">{district.financial_progress_percentage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(district.financial_progress_percentage, 100)}%` }}
                            ></div>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Physical Progress</span>
                            <span className="font-medium">{district.physical_progress_percentage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(district.physical_progress_percentage, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'variance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cost Variance Analysis - Top 20 Works by Escalation
                </h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Works with significant cost escalations or expenditure variances may require review.
                      Green indicates under budget, yellow indicates moderate variance, red indicates significant overruns.
                    </p>
                  </div>
                </div>
                <CostVarianceAnalysis 
                  data={performanceData?.costVarianceAnalysis || []} 
                  loading={loading}
                />
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Performance Highlights</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-green-600">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {overviewMetrics.completedWorks} works completed successfully
                </li>
                <li className="flex items-center text-blue-600">
                  <Activity className="w-4 h-4 mr-2" />
                  {overviewMetrics.inProgressWorks} works currently in progress
                </li>
                <li className="flex items-center text-orange-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {overviewMetrics.utilizationRate.toFixed(1)}% budget utilization
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Areas for Attention</h4>
              <ul className="space-y-2 text-sm">
                {overviewMetrics.delayedWorks > 0 && (
                  <li className="flex items-center text-red-600">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {overviewMetrics.delayedWorks} works past deadline
                  </li>
                )}
                {performanceData?.costVarianceAnalysis && (
                  <li className="flex items-center text-yellow-600">
                    <DollarSign className="w-4 h-4 mr-2" />
                    {performanceData.costVarianceAnalysis.filter(w => w.escalation_percentage > 20).length} high escalation works
                  </li>
                )}
                {progressData?.completionForecast && (
                  <li className="flex items-center text-blue-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    {progressData.completionForecast.total_works_in_progress} works need monitoring
                  </li>
                )}
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Recommendations</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-gray-600">
                  <Target className="w-4 h-4 mr-2" />
                  Focus on delayed project recovery
                </li>
                <li className="flex items-center text-gray-600">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Monitor high-variance projects
                </li>
                <li className="flex items-center text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  Review contractor performance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;